import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatOption} from '@angular/material/core';
import {ClassificationType, ReferenceBookType} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, formatDateTimeFromDateToUi, LocalizationService, Spinner} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {combineLatest, debounceTime, distinctUntilChanged, Subscription} from 'rxjs';
import {DateRangeForm} from '../../../../components/date-range-picker-field/date-range-picker-field.component';
import {SELECT_ALL_VALUE} from '../../../../components/dictionary/dictionary-selector.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService} from '../../../../domain/dictionary-holder.service';

@Component({
    selector: 'af-dashboard-filter',
    templateUrl: './dashboard-filter.component.html',
    styleUrl: './dashboard-filter.component.scss',
    standalone: false,
})
export class DashboardFilterComponent implements OnInit {
    @ViewChild('selectAll') selectAll: MatOption;
    @Input() label: string;
    @Input() showLabelFromSelect: boolean = false;
    @Input() showDate = true;
    @Input() showDateRangePicker = false;
    @Input() showDateRangeComparison = false;
    @Input() showOrganizations = true;
    @Input() showChips = false;
    @Input() hasTime = true;
    @Input() maxDate = new Date();
    @Input() hasPickerActions = true;
    @Input() filterOrganization: OrganizationType[];
    @Input() allOrganizationLabel: 'organizations' | 'mvd' = 'organizations';
    @Input() chips: ClassificationType[] = [];
    @Input() chipsFormControlName: string;
    @Input() subtractMonths: number;
    @Input() shouldUseFullMonthSubstraction: boolean;
    @Output() dataChangeListener = new EventEmitter<CommonDashboardFilterValue>;
    Spinner = Spinner;
    form: FormGroup<CommonDashboardFilterForm>;
    currentDateTime = formatDateTimeFromDateToUi(new Date());
    formChangesSubscription: Subscription;
    currentPeriodEndDate = dayjs();
    currentPeriodStartDate = this.currentPeriodEndDate.subtract(1, 'month');
    previousPeriodEndDate = this.currentPeriodStartDate.subtract(1, 'day');
    previousPeriodStartDate = this.previousPeriodEndDate.subtract(1, 'month');
    hasFullAccessRoles: boolean;

    constructor(
        private formBuilder: FormBuilder,
        private authHolderService: AuthHolderService,
        private dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
        private cdr: ChangeDetectorRef,
    ) {
        this.hasFullAccessRoles = this.authHolderService.hasAccess(['npck-admin', 'government-user']);
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({});
        this.currentPeriodStartDate = this.currentPeriodEndDate.subtract(this.subtractMonths ?? 1, 'month');
        this.previousPeriodStartDate = this.previousPeriodEndDate.subtract(this.subtractMonths ?? 1, 'month');
        if (this.shouldUseFullMonthSubstraction) {
            this.currentPeriodEndDate = dayjs().subtract(1, 'month').endOf('month');
            this.currentPeriodStartDate = this.currentPeriodEndDate.subtract(this.subtractMonths, 'months').startOf('month');
            this.maxDate = this.currentPeriodEndDate.toDate();
        }
        if (this.showDateRangePicker) {
            this.form.addControl(`dateRange`, this.formBuilder.group({}));
        }
        if (this.showChips) {
            this.form.addControl(`classificationBy`, new FormControl(this.chips[0]));
        }
        if (this.showOrganizations) {
            if (this.hasFullAccessRoles) {
                this.form.addControl('organizationTypes', new FormControl([SELECT_ALL_VALUE]));
                this.form.addControl('organizations', new FormControl([SELECT_ALL_VALUE]));
            } else {
                this.form.addControl('organization', new FormControl(SELECT_ALL_VALUE));
            }
            if (this.showLabelFromSelect) {
                this.label = this.localizationService.getLocalizedKey(`i18n_af_labelAllOrganization#${this.allOrganizationLabel}`);
            }
        }
        if (this.showDateRangeComparison) {
            const dateRangeComparisonForm: FormGroup<DateRangesComparisonForm> = this.formBuilder.group({
                dateRangePreviousPeriod: this.formBuilder.group({}),
                dateRangeCurrentPeriod: this.formBuilder.group({}),
            });
            this.form.addControl(`dateRangeComparison`, dateRangeComparisonForm);
        }
        if (this.filterOrganization) {
            !this.hasFullAccessRoles && this.filterOrganization.push(this.authHolderService.organizationBin);
        } else {
            this.filterOrganization = !this.hasFullAccessRoles ? [this.authHolderService.organizationBin] : this.filterOrganization;
        }
        this.subscribeToFormChanges();
        this.cdr.detectChanges();
    }

    subscribeToFormChanges(): void {
        this.formChangesSubscription = combineLatest([
            this.form.valueChanges.pipe(
                debounceTime(500),
                distinctUntilChanged((prev, curr) => {
                    const hasDateRange = !!Object.keys(prev.dateRange ?? {}).length && !!Object.keys(curr.dateRange ?? {}).length;
                    const isStartDateTheSame = this.showDateRangePicker
                        ? hasDateRange && prev.dateRange?.dateFrom?.getTime() === curr.dateRange?.dateFrom?.getTime()
                        : true;
                    const isEndDateTheSame = this.showDateRangePicker
                        ? hasDateRange && prev.dateRange?.dateTo?.getTime() === curr.dateRange?.dateTo?.getTime()
                        : true;
                    const isClassificationByTheSame = prev.classificationBy === curr.classificationBy;
                    const isOrganizationTheSame = this.showOrganizations ? prev.organization === curr.organization : true;
                    const isOrganizationsTheSame = this.showOrganizations ? (
                        prev.organizations?.length === curr.organizations?.length &&
                        prev.organizations?.every(org => curr.organizations.includes(org))
                    ) : true;
                    return isStartDateTheSame
                        && isEndDateTheSame
                        && isClassificationByTheSame
                        && isOrganizationTheSame
                        && isOrganizationsTheSame;
                })),
            this.dictionaryHolderService.getReference(ReferenceBookType.BIN.toLowerCase())])
            .subscribe(([value, binDictionary]) => {
                let organizations: string[];
                if (this.form.controls.organizations && value.organizations?.length) {
                    organizations = value.organizations.filter(item => item !== SELECT_ALL_VALUE);
                } else if (this.form.controls.organization && value.organization !== SELECT_ALL_VALUE) {
                    organizations = value.organization ? [value.organization] : null;
                }
                if (this.showLabelFromSelect) {
                    this.label = binDictionary.find(item => item.code === value.organization || value.organizations?.includes(item.code))
                        ?.name_ru ?? this.localizationService.getLocalizedKey(`i18n_af_labelAllOrganization#${this.allOrganizationLabel}`);
                }
                this.dataChangeListener.emit({
                    classificationBy: this.chipsFormControlName ? this.form.get(this.chipsFormControlName)?.value : undefined,
                    dateRange: this.showDateRangePicker ? {
                        dateFrom: dayjs(value.dateRange.dateFrom).format(DATE_TIME_SERVER_FORMAT),
                        dateTo: dayjs(value.dateRange.dateTo).format(DATE_TIME_SERVER_FORMAT),
                    } : null,
                    dateRangeComparison: this.showDateRangeComparison ? {
                        dateRangePreviousPeriod: {
                            dateFrom: dayjs(value.dateRangeComparison.dateRangePreviousPeriod.dateFrom).format(DATE_TIME_SERVER_FORMAT),
                            dateTo: dayjs(value.dateRangeComparison.dateRangePreviousPeriod.dateTo).format(DATE_TIME_SERVER_FORMAT),
                        },
                        dateRangeCurrentPeriod: {
                            dateFrom: dayjs(value.dateRangeComparison.dateRangeCurrentPeriod.dateFrom).format(DATE_TIME_SERVER_FORMAT),
                            dateTo: dayjs(value.dateRangeComparison.dateRangeCurrentPeriod.dateTo).format(DATE_TIME_SERVER_FORMAT),
                        },
                    } : null,
                    organizations: organizations,
                });
            });
        this.form.updateValueAndValidity();
    }

    onChipSelectionChange(chip: ClassificationType): void {
        (this.form.get(this.chipsFormControlName) as FormGroup<ClassificationType>).setValue(chip);
    }
}

export declare type OrganizationType = string | 'BANK' | 'PAYM' | 'MFO' | 'OPERATOR' | 'IO' | 'ALL';

export interface CommonDashboardFilterValue {
    classificationBy?: ClassificationType;
    dateRange?: DateRangeValue;
    dateRangeComparison?: DateRangesComparisonValue,
    organizations?: string[];
}

export interface DateRangeValue {
    dateFrom: string;
    dateTo: string;
}

interface DateRangesComparisonValue {
    dateRangePreviousPeriod?: DateRangeValue;
    dateRangeCurrentPeriod?: DateRangeValue;
}

interface DateRangesComparisonForm {
    dateRangePreviousPeriod: FormGroup<DateRangeForm>;
    dateRangeCurrentPeriod: FormGroup<DateRangeForm>;
}

interface CommonDashboardFilterForm {
    classificationBy?: FormControl<ClassificationType>;
    dateRange?: FormGroup<DateRangeForm>;
    dateRangeComparison?: FormGroup<DateRangesComparisonForm>;
    organizationTypes?: FormControl<string[]>
    organizations?: FormControl<string[]>;
    organization?: FormControl<string>;
}
